import { z } from 'zod'

import { http } from '@/shared/api'

import type {
  Campaign,
  CampaignDetail,
  CampaignFilters,
  CampaignReport,
  CampaignSources,
  PageType,
  StatsGranularity,
} from '../model/types'
import { SOURCE_KEYS } from '../model/types'

const campaignItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  is_active: z.boolean(),
  white_only: z.boolean(),
  created_at: z.string(),
})

const listSchema = z.object({ items: z.array(campaignItemSchema).nullable() })

const toCampaign = (item: z.infer<typeof campaignItemSchema>): Campaign => ({
  id: item.id,
  name: item.name,
  isActive: item.is_active,
  whiteOnly: item.white_only,
  createdAt: item.created_at,
})

const countSchema = z.object({ count: z.number() })

/**
 * How many campaigns someone has.
 *
 * Its own request rather than a field on the subscription, though the two are
 * drawn side by side: a subscription is a plan and a period, and campaigns are
 * what has been made under it. They travelled together until an endpoint named
 * `subscriptions` was answering with a campaign count, which no name could
 * explain.
 *
 * `userId` is for an administrator asking about someone else; without it the
 * backend counts whoever holds the token.
 */
export async function fetchCampaignCount(userId?: string, signal?: AbortSignal): Promise<number> {
  const { count } = await http.get(userId === undefined ? 'campaigns/count' : 'admin/campaigns/count', {
    searchParams: userId === undefined ? undefined : { user_id: userId },
    schema: countSchema,
    signal,
  })

  return count
}

export async function fetchCampaigns(signal?: AbortSignal): Promise<Campaign[]> {
  const { items } = await http.get('campaigns', { schema: listSchema, signal })
  return (items ?? []).map(toCampaign)
}

const detailSchema = campaignItemSchema.extend({ updated_at: z.string() })

export async function fetchCampaign(id: string, signal?: AbortSignal): Promise<CampaignDetail> {
  const raw = await http.get(`campaigns/${id}`, { schema: detailSchema, signal })
  return { ...toCampaign(raw), updatedAt: raw.updated_at }
}

const createdSchema = z.object({ id: z.string(), name: z.string() })

export async function createCampaign(name: string): Promise<{ id: string; name: string }> {
  return http.post('campaigns', { json: { name }, schema: createdSchema })
}

export type CampaignPatch = Partial<Pick<Campaign, 'name' | 'isActive' | 'whiteOnly'>>

export async function updateCampaign(id: string, patch: CampaignPatch): Promise<void> {
  await http.patch(`campaigns/${id}`, {
    json: {
      ...(patch.name !== undefined && { name: patch.name }),
      ...(patch.isActive !== undefined && { is_active: patch.isActive }),
      ...(patch.whiteOnly !== undefined && { white_only: patch.whiteOnly }),
    },
  })
}

export async function deleteCampaign(id: string): Promise<void> {
  await http.delete(`campaigns/${id}`)
}

/**
 * The campaign's integration script, the `index.php` a client drops at their
 * landing's root. The backend serves it as an attachment behind the same bearer
 * auth as everything else, so it goes through `getBlob` rather than an anchor.
 */
export async function downloadCampaignServePhp(
  id: string,
): Promise<{ blob: Blob; filename: string }> {
  return http.getBlob(`campaigns/${id}/serve.php`, { fallbackName: 'index.php' })
}

/* ── Sources ───────────────────────────────────────────────────────────── */

const sourcesSchema = z.object(
  Object.fromEntries(SOURCE_KEYS.map((key) => [key, z.boolean()])) as Record<
    (typeof SOURCE_KEYS)[number],
    z.ZodBoolean
  >,
)

export async function fetchCampaignSources(
  id: string,
  signal?: AbortSignal,
): Promise<CampaignSources> {
  return http.get(`campaigns/${id}/sources`, { schema: sourcesSchema, signal })
}

/** Full replace: the backend zeroes anything omitted, so all 15 always go. */
export async function updateCampaignSources(id: string, sources: CampaignSources): Promise<void> {
  await http.patch(`campaigns/${id}/sources`, { json: sources })
}

/* ── Filters ───────────────────────────────────────────────────────────── */

const pageTypeSchema = z.enum(['redirect', 'include'])

const filtersSchema = z.object({
  white_type: pageTypeSchema,
  white_page: z.string(),
  black_type: pageTypeSchema,
  black_page: z.string(),
  blacklist_ip: z.array(z.string()).nullable(),
  blacklist_isp: z.array(z.string()).nullable(),
  blacklist_dns: z.array(z.string()).nullable(),
  allowed_countries: z.array(z.string()).nullable(),
  allowed_languages: z.array(z.string()).nullable(),
  allowed_devices: z.array(z.string()).nullable(),
  allowed_os: z.array(z.string()).nullable(),
  custom_utm: z.array(z.string()).nullable(),
  custom_sources: z.array(z.string()).nullable(),
  allow_vpn: z.boolean(),
})

export async function fetchCampaignFilters(
  id: string,
  signal?: AbortSignal,
): Promise<CampaignFilters> {
  const raw = await http.get(`campaigns/${id}/filters`, { schema: filtersSchema, signal })

  return {
    whiteType: raw.white_type as PageType,
    whitePage: raw.white_page,
    blackType: raw.black_type as PageType,
    blackPage: raw.black_page,
    blacklistIp: raw.blacklist_ip ?? [],
    blacklistIsp: raw.blacklist_isp ?? [],
    blacklistDns: raw.blacklist_dns ?? [],
    allowedCountries: raw.allowed_countries ?? [],
    allowedLanguages: raw.allowed_languages ?? [],
    allowedDevices: raw.allowed_devices ?? [],
    allowedOs: raw.allowed_os ?? [],
    customUtm: raw.custom_utm ?? [],
    customSources: raw.custom_sources ?? [],
    allowVpn: raw.allow_vpn,
  }
}

/** Full replace, and `white_type`/`black_type` are mandatory on the wire. */
export async function updateCampaignFilters(id: string, filters: CampaignFilters): Promise<void> {
  await http.patch(`campaigns/${id}/filters`, {
    json: {
      white_type: filters.whiteType,
      white_page: filters.whitePage,
      black_type: filters.blackType,
      black_page: filters.blackPage,
      blacklist_ip: filters.blacklistIp,
      blacklist_isp: filters.blacklistIsp,
      blacklist_dns: filters.blacklistDns,
      allowed_countries: filters.allowedCountries,
      allowed_languages: filters.allowedLanguages,
      allowed_devices: filters.allowedDevices,
      allowed_os: filters.allowedOs,
      custom_utm: filters.customUtm,
      custom_sources: filters.customSources,
      allow_vpn: filters.allowVpn,
    },
  })
}

/* ── Stats ─────────────────────────────────────────────────────────────── */

const sliceSchema = z.object({ key: z.string(), hits: z.number(), leads: z.number() })

// The endpoint also returns `series` and `reasons`. Nothing draws either, and
// zod drops what the schema does not name, so they are parsed away here rather
// than carried around.
const reportSchema = z.object({
  range: z.object({
    from: z.string(),
    to: z.string(),
    granularity: z.enum(['hour', 'day']),
  }),
  matrix: z.array(z.object({ type: z.string(), page: z.string(), hits: z.number() })).nullable(),
  devices: z.array(sliceSchema).nullable(),
  countries: z.array(sliceSchema).nullable(),
  sources: z.array(sliceSchema).nullable(),
})

/**
 * The window's cross-tab and its breakdowns, in one read. Empty collections
 * come back as `null` from Go, which is not the same thing as absent — every
 * one of them is normalised here so the view never branches on it.
 */
export async function fetchCampaignReport(
  id: string,
  params: { from?: string; to?: string; granularity?: StatsGranularity },
  signal?: AbortSignal,
): Promise<CampaignReport> {
  const raw = await http.get(`campaigns/${id}/stats`, {
    searchParams: {
      ...(params.from !== undefined && { from: params.from }),
      ...(params.to !== undefined && { to: params.to }),
      ...(params.granularity !== undefined && { granularity: params.granularity }),
    },
    schema: reportSchema,
    signal,
  })

  return {
    range: raw.range,
    matrix: raw.matrix ?? [],
    devices: raw.devices ?? [],
    countries: raw.countries ?? [],
    sources: raw.sources ?? [],
  }
}
